import { useState } from "react";
import { X, ChevronRight, ChevronLeft, Calculator, CheckCircle, Info, User, Dumbbell, Apple } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Config ────────────────────────────────────────────────────────────────────
const DOELEN = [
  { value: "afvallen",    label: "Afvallen",     desc: "Calorietekort van 500 kcal" },
  { value: "onderhoud",   label: "Onderhoud",    desc: "Gewicht stabiel houden" },
  { value: "spieropbouw", label: "Spieropbouw",  desc: "Lean muscle, kleine surplus" },
  { value: "aankomen",    label: "Aankomen",     desc: "Calorie-surplus van 400 kcal" },
];

const TRAINING_TYPEN = [
  { value: "krachttraining", label: "Krachttraining" },
  { value: "crossfit",       label: "CrossFit" },
  { value: "hiit",           label: "HIIT" },
  { value: "conditie",       label: "Conditie" },
  { value: "cardio",         label: "Cardio" },
  { value: "gemengd",        label: "Gemengd" },
  { value: "team_sport",     label: "Teamsport" },
  { value: "yoga",           label: "Yoga" },
  { value: "pilates",        label: "Pilates" },
  { value: "anders",         label: "Anders" },
];

const ACTIVITEIT_OPTIES = [
  { value: "sedentair",  label: "Sedentair",    desc: "Bureauwerk, weinig lopen" },
  { value: "licht",      label: "Licht actief", desc: "Af en toe lopen" },
  { value: "matig",      label: "Matig actief", desc: "Regelmatig lopen/fietsen" },
  { value: "actief",     label: "Actief",       desc: "Lichamelijk werk of veel staan" },
  { value: "erg_actief", label: "Erg actief",   desc: "Zwaar fysiek werk" },
];

const STEPS = ["Persoonsgegevens", "Training & Activiteit", "Voeding & Allergieën", "Resultaat"];

// ── ResultaatTab ──────────────────────────────────────────────────────────────
function ResultaatTab({ berekening, intakeData }) {
  if (!berekening) return null;
  const {
    bmr, onderhoudsbehoefte, activiteit_factor, activiteit_label,
    correctie_doel, correctie_label, calorie_doel,
    eiwitten_doel, eiwitten_per_kg, koolhydraten_doel, vetten_doel,
    formule, uitleg,
  } = berekening;

  const MACROS = [
    { label: "Eiwitten",      waarde: eiwitten_doel,      unit: "g/dag",   kleur: "#60a5fa", pct: Math.round(eiwitten_doel * 4 / calorie_doel * 100) },
    { label: "Koolhydraten",  waarde: koolhydraten_doel,  unit: "g/dag",   kleur: "#34d399", pct: Math.round(koolhydraten_doel * 4 / calorie_doel * 100) },
    { label: "Vetten",        waarde: vetten_doel,        unit: "g/dag",   kleur: "#f97316", pct: Math.round(vetten_doel * 9 / calorie_doel * 100) },
  ];

  const doel_naam = DOELEN.find(d => d.value === intakeData.doel)?.label || intakeData.doel;
  const correctie_teken = correctie_doel > 0 ? "+" : "";

  return (
    <div className="space-y-4">
      {/* Caloriedoel groot */}
      <div className="bg-gradient-to-br from-[#C9A84C]/15 to-[#C9A84C]/5 border border-[#C9A84C]/30 rounded-2xl p-5 text-center">
        <p className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-1">Aanbevolen calorieën</p>
        <p className="text-5xl font-black text-white tabular-nums">{calorie_doel}</p>
        <p className="text-[#C9A84C] text-sm font-semibold mt-0.5">kcal per dag</p>
      </div>

      {/* Berekening stappen */}
      <div className="bg-[#111] border border-white/6 rounded-xl p-4 space-y-2.5">
        <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-3">Berekening</p>
        {[
          { label: "Ruststofwisseling (BMR)", waarde: `${bmr} kcal`, sub: formule },
          { label: "Activiteitsfactor", waarde: `× ${activiteit_factor}`, sub: activiteit_label.split("—")[1]?.trim() },
          { label: "Onderhoudsbehoefte", waarde: `${onderhoudsbehoefte} kcal`, sub: "BMR × activiteitsfactor" },
          {
            label: `Correctie (${doel_naam})`,
            waarde: `${correctie_teken}${correctie_doel} kcal`,
            sub: correctie_label,
            kleur: correctie_doel < 0 ? "#f87171" : correctie_doel > 0 ? "#4ade80" : "#71717a"
          },
        ].map((r, i) => (
          <div key={i} className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold">{r.label}</p>
              {r.sub && <p className="text-zinc-500 text-xs mt-0.5 leading-snug">{r.sub}</p>}
            </div>
            <p className="text-sm font-bold tabular-nums shrink-0" style={{ color: r.kleur || "#C9A84C" }}>
              {r.waarde}
            </p>
          </div>
        ))}
        <div className="border-t border-white/8 pt-2.5 flex items-center justify-between">
          <p className="text-white font-black">Doelcalorieën</p>
          <p className="text-[#C9A84C] font-black text-lg tabular-nums">{calorie_doel} kcal</p>
        </div>
      </div>

      {/* Macro's */}
      <div className="bg-[#111] border border-white/6 rounded-xl p-4">
        <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-3">Macro-verdeling</p>
        <div className="space-y-3">
          {MACROS.map(m => (
            <div key={m.label}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: m.kleur }} />
                  <span className="text-white text-sm font-semibold">{m.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">{m.pct}%</span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: m.kleur }}>
                    {m.waarde} {m.unit}
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-white/6 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${m.pct}%`, background: m.kleur }} />
              </div>
            </div>
          ))}
        </div>
        <p className="text-zinc-600 text-xs mt-3">
          Eiwit: {eiwitten_per_kg}g per kg lichaamsgewicht
        </p>
      </div>

      {/* Uitleg */}
      <div className="bg-[#111] border border-white/6 rounded-xl p-4">
        <div className="flex items-start gap-2.5">
          <Info size={14} className="text-[#C9A84C] mt-0.5 shrink-0" />
          <div>
            <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-2">Toelichting berekening</p>
            <p className="text-zinc-400 text-sm leading-relaxed">{uitleg}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── HOOFD MODAL ───────────────────────────────────────────────────────────────
export default function VoedingIntakeModal({
  token,
  sporter,
  initialData,
  initialBerekening,
  onClose,
  onSaved,
  adminEndpoint,   // optioneel — als opgegeven, gebruik dit ipv de standaard endpoints
}) {
  const isCoach = !!sporter;
  const sporterId = sporter?.id;
  const [stap, setStap] = useState(initialData ? 3 : 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [berekening, setBerekening] = useState(initialBerekening || null);

  const defaultForm = {
    leeftijd: "", geslacht: "man", lengte: "", gewicht: "",
    doel: "onderhoud", sportdagen_per_week: 3, type_training: "gemengd",
    activiteit_buiten_sport: "matig",
    allergieen: "", voedingsvoorkeuren: "", producten_niet_eten: "", maaltijden_per_dag: 3,
  };

  const [form, setForm] = useState(initialData ? {
    ...defaultForm,
    ...initialData,
    leeftijd: initialData.leeftijd || "",
    lengte:   initialData.lengte || "",
    gewicht:  initialData.gewicht || "",
  } : defaultForm);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.leeftijd || !form.lengte || !form.gewicht) {
      setError("Vul alle verplichte velden in (leeftijd, lengte, gewicht)");
      setStap(0);
      return;
    }
    setSaving(true); setError("");
    const payload = { ...form,
      leeftijd: parseInt(form.leeftijd),
      lengte:   parseFloat(form.lengte),
      gewicht:  parseFloat(form.gewicht),
      sportdagen_per_week: parseInt(form.sportdagen_per_week),
      maaltijden_per_dag:  parseInt(form.maaltijden_per_dag),
    };
    const url = adminEndpoint
      ? `${API}${adminEndpoint}`
      : isCoach
        ? `${API}/api/nutrition/sporters/${sporterId}/intake`
        : `${API}/api/nutrition/intake`;
    try {
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      setBerekening(d.berekening);
      setStap(3);
      onSaved && onSaved(d.berekening);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const inp = "bg-[#0a0a0a] border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 w-full placeholder-zinc-700";
  const label = "text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block";

  const isLaatsteFormStap = stap === 2;
  const toonResultaat = stap === 3;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 shrink-0">
          <div>
            <h3 className="text-white font-black text-base">Voedingsprofiel</h3>
            {isCoach && sporter && (
              <p className="text-zinc-500 text-xs mt-0.5">voor {sporter.naam || sporter.name}</p>
            )}
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-white" /></button>
        </div>

        {/* Stap indicator (alleen bij formulier) */}
        {!toonResultaat && (
          <div className="flex gap-1 px-5 pt-4 shrink-0">
            {STEPS.slice(0, 3).map((s, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <div className={`w-full h-1 rounded-full transition-all ${i <= stap ? "bg-[#C9A84C]" : "bg-white/8"}`} />
                <span className={`text-[9px] font-bold uppercase tracking-wide hidden sm:block ${i === stap ? "text-[#C9A84C]" : "text-zinc-600"}`}>
                  {s}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && (
            <div className="bg-red-950/60 border border-red-500/30 rounded-xl px-4 py-3 text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Stap 0 — Persoonsgegevens */}
          {stap === 0 && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <User size={14} className="text-[#C9A84C]" />
                <p className="text-white font-bold text-sm">Persoonsgegevens</p>
              </div>

              {/* Geslacht */}
              <div>
                <label className={label}>Geslacht *</label>
                <div className="flex gap-2">
                  {[{v:"man",l:"Man"},{v:"vrouw",l:"Vrouw"}].map(g => (
                    <button key={g.v} onClick={() => set("geslacht", g.v)} data-testid={`geslacht-${g.v}`}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${form.geslacht === g.v ? "text-black" : "bg-white/6 border border-white/10 text-zinc-400 hover:text-white"}`}
                      style={form.geslacht === g.v ? { background: "#C9A84C" } : {}}>
                      {g.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Leeftijd / Lengte / Gewicht */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { k: "leeftijd", l: "Leeftijd *", ph: "25", su: "jaar" },
                  { k: "lengte",   l: "Lengte *",   ph: "175", su: "cm" },
                  { k: "gewicht",  l: "Gewicht *",  ph: "75", su: "kg" },
                ].map(f => (
                  <div key={f.k}>
                    <label className={label}>{f.l}</label>
                    <div className="relative">
                      <input type="number" value={form[f.k]} min={0} step={f.k === "gewicht" || f.k === "lengte" ? 0.1 : 1}
                        onChange={e => set(f.k, e.target.value)}
                        data-testid={`intake-${f.k}`}
                        placeholder={f.ph} className={inp} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 text-xs pointer-events-none">{f.su}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Doel */}
              <div>
                <label className={label}>Doel *</label>
                <div className="grid grid-cols-2 gap-2">
                  {DOELEN.map(d => (
                    <button key={d.value} onClick={() => set("doel", d.value)} data-testid={`doel-${d.value}`}
                      className={`px-3 py-2.5 rounded-xl text-left transition-all ${form.doel === d.value ? "border-[#C9A84C]/50" : "border-white/8 hover:border-white/20"} border`}
                      style={{ background: form.doel === d.value ? "#C9A84C18" : "#111" }}>
                      <p className={`text-sm font-bold ${form.doel === d.value ? "text-[#C9A84C]" : "text-white"}`}>{d.label}</p>
                      <p className="text-zinc-500 text-[10px] mt-0.5 leading-tight">{d.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Stap 1 — Training & Activiteit */}
          {stap === 1 && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Dumbbell size={14} className="text-[#C9A84C]" />
                <p className="text-white font-bold text-sm">Training & Activiteit</p>
              </div>

              {/* Sportdagen */}
              <div>
                <label className={label}>Sportdagen per week</label>
                <div className="flex gap-1.5 flex-wrap">
                  {[0,1,2,3,4,5,6,7].map(n => (
                    <button key={n} onClick={() => set("sportdagen_per_week", n)} data-testid={`sportdagen-${n}`}
                      className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${form.sportdagen_per_week === n ? "text-black" : "bg-white/6 border border-white/10 text-zinc-400 hover:text-white"}`}
                      style={form.sportdagen_per_week === n ? { background: "#C9A84C" } : {}}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Type training */}
              <div>
                <label className={label}>Type training</label>
                <div className="grid grid-cols-2 gap-2">
                  {TRAINING_TYPEN.map(t => (
                    <button key={t.value} onClick={() => set("type_training", t.value)} data-testid={`training-${t.value}`}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold text-left transition-all ${form.type_training === t.value ? "text-black" : "bg-white/6 border border-white/10 text-zinc-400 hover:text-white"}`}
                      style={form.type_training === t.value ? { background: "#C9A84C" } : {}}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Activiteit buiten sport */}
              <div>
                <label className={label}>Activiteitsniveau buiten sport</label>
                <div className="space-y-1.5">
                  {ACTIVITEIT_OPTIES.map(a => (
                    <button key={a.value} onClick={() => set("activiteit_buiten_sport", a.value)} data-testid={`activiteit-${a.value}`}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all ${form.activiteit_buiten_sport === a.value ? "border-[#C9A84C]/50" : "border-white/8 hover:border-white/15"} border`}
                      style={{ background: form.activiteit_buiten_sport === a.value ? "#C9A84C18" : "#111" }}>
                      <div className="text-left">
                        <p className={`text-sm font-semibold ${form.activiteit_buiten_sport === a.value ? "text-[#C9A84C]" : "text-white"}`}>{a.label}</p>
                        <p className="text-zinc-500 text-xs">{a.desc}</p>
                      </div>
                      {form.activiteit_buiten_sport === a.value && <CheckCircle size={14} className="text-[#C9A84C] shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Stap 2 — Voeding & Allergieën */}
          {stap === 2 && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Apple size={14} className="text-[#C9A84C]" />
                <p className="text-white font-bold text-sm">Voeding & Allergieën</p>
              </div>

              <div>
                <label className={label}>Allergieën</label>
                <input value={form.allergieen} onChange={e => set("allergieen", e.target.value)}
                  data-testid="intake-allergieen"
                  placeholder="bijv. lactose, gluten, noten..." className={inp} />
              </div>

              <div>
                <label className={label}>Voedingsvoorkeuren</label>
                <input value={form.voedingsvoorkeuren} onChange={e => set("voedingsvoorkeuren", e.target.value)}
                  data-testid="intake-voedingsvoorkeuren"
                  placeholder="bijv. vegetarisch, weinig suiker..." className={inp} />
              </div>

              <div>
                <label className={label}>Producten die ik niet eet</label>
                <input value={form.producten_niet_eten} onChange={e => set("producten_niet_eten", e.target.value)}
                  data-testid="intake-producten-niet"
                  placeholder="bijv. zalm, broccoli, rood vlees..." className={inp} />
              </div>

              <div>
                <label className={label}>Aantal maaltijden per dag</label>
                <div className="flex gap-2">
                  {[2,3,4,5,6].map(n => (
                    <button key={n} onClick={() => set("maaltijden_per_dag", n)} data-testid={`maaltijden-${n}`}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all ${form.maaltijden_per_dag === n ? "text-black" : "bg-white/6 border border-white/10 text-zinc-400"}`}
                      style={form.maaltijden_per_dag === n ? { background: "#C9A84C" } : {}}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Stap 3 — Resultaat */}
          {stap === 3 && (
            <ResultaatTab berekening={berekening} intakeData={form} />
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/8 shrink-0">
          {toonResultaat ? (
            <div className="flex gap-3">
              <button onClick={() => setStap(0)}
                className="flex-1 py-3 rounded-xl border border-white/10 text-zinc-300 text-sm font-semibold hover:text-white hover:border-white/20 transition-all">
                Aanpassen
              </button>
              <button onClick={onClose}
                className="flex-1 py-3 rounded-xl text-black text-sm font-bold"
                style={{ background: "#C9A84C" }}>
                Klaar
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              {stap > 0 && (
                <button onClick={() => setStap(s => s - 1)}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:text-white transition-all">
                  <ChevronLeft size={14} /> Terug
                </button>
              )}
              {!isLaatsteFormStap ? (
                <button onClick={() => setStap(s => s + 1)} data-testid="intake-volgende-btn"
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-black text-sm font-bold"
                  style={{ background: "#C9A84C" }}>
                  Volgende <ChevronRight size={14} />
                </button>
              ) : (
                <button onClick={handleSave} disabled={saving} data-testid="intake-opslaan-btn"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-black text-sm font-bold disabled:opacity-40"
                  style={{ background: "#C9A84C" }}>
                  <Calculator size={14} />
                  {saving ? "Berekenen..." : "Berekenen & Opslaan"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
